import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { BudgetLine } from "@torre/expenses";
import { useTheme } from "../../../context/ThemeContext";
import { fetchAccess, fetchBudgets, fetchExpectedIncome, type ExpectedIncomeResponse } from "../services/expensesService";
import { BUDGETS_PATH, INCOME_PATH } from "../lib/flowRoutes";
import { currentMonth, money, monthLabel, shiftMonth } from "../lib/format";
import { monthBudget } from "../lib/projectedFlow";
import { ui } from "../theme";

// CRM → Pronóstico → vínculo con Finanzas (#1290): lo que el CRM espera que
// entre en los próximos meses (contratos fijos, por cobrar y pipeline ponderado,
// con las cotizaciones enviadas, #1546) contra el gasto presupuestado. Solo la ve quien tiene acceso a Finanzas: sin
// acceso no pinta nada y no pide datos de gastos.

/** El mes en curso y los dos siguientes. */
const MONTHS_AHEAD = 3;

export default function FinanceLinkCard() {
  const { t, i18n } = useTranslation(["common", "expensesStatements"]);
  const tf = (k: string, o?: Record<string, unknown>) => t(`expensesStatements:flow.${k}`, o);
  const language = i18n.language;
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const [data, setData] = useState<ExpectedIncomeResponse | null>(null);
  const [lines, setLines] = useState<BudgetLine[]>([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // `GET /expensesAccess` responde 200 con `allowed: false`: no provoca un 403.
        const access = await fetchAccess();
        if (!alive || !access.allowed) return;
        const from = currentMonth();
        const [income, budgets] = await Promise.all([fetchExpectedIncome({ from, to: shiftMonth(from, MONTHS_AHEAD - 1) }), fetchBudgets()]);
        if (!alive) return;
        setLines(budgets);
        setData(income);
      } catch {
        // Es una tarjeta de apoyo: si falla, el pronóstico se queda como estaba.
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const rows = useMemo(
    () =>
      (data?.months || []).map((m) => {
        const income = m.phase === "current" ? m.actual + m.expected : m.expected;
        const budget = monthBudget(lines, m.period);
        // Lo que depende de cerrar: el pipeline y las cotizaciones enviadas (#1546).
        const forecast = m.pipeline + (m.quotes || 0);
        return { ...m, income, budget, forecast, net: income - budget };
      }),
    [data, lines],
  );
  if (!data) return null;
  const fmt = (v: number) => money(v, "MXN", language);

  return (
    <section aria-label={tf("crm.title")} className={`${c.card} mt-6 space-y-3`} data-testid="finance-link">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className={`text-sm font-semibold ${c.text}`}>
            <i className="ri-exchange-funds-line mr-1 text-teal-600" />
            {tf("crm.title")}
          </h2>
          <p className={`text-xs ${c.muted}`}>{tf("crm.subtitle", { fixed: fmt(data.monthlyFixed), count: data.contracts.length })}</p>
        </div>
        <div className="flex flex-wrap gap-3 text-xs">
          <a href={BUDGETS_PATH} className="text-teal-600 hover:underline">
            <i className="ri-scales-3-line mr-1" />
            {tf("links.budgets")}
          </a>
          <a href={INCOME_PATH} className="text-teal-600 hover:underline">
            <i className="ri-arrow-down-circle-line mr-1" />
            {tf("links.income")}
          </a>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className={`text-left text-xs ${c.muted}`}>
              <th className="py-1 pr-2">{tf("table.month")}</th>
              <th className="py-1 pr-2 text-right">{tf("crm.firm")}</th>
              <th className="py-1 pr-2 text-right">{tf("series.pipeline")}</th>
              <th className="py-1 pr-2 text-right">{tf("income.expected")}</th>
              <th className="py-1 pr-2 text-right">{tf("crm.budget")}</th>
              <th className="py-1 text-right">{tf("table.net")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => (
              <tr key={m.period} className={`border-t ${c.border} ${c.text}`} data-testid={`finance-${m.period}`}>
                <td className="whitespace-nowrap py-1.5 pr-2">{monthLabel(m.period, language)}</td>
                <td className="py-1.5 pr-2 text-right tabular-nums">{fmt(m.income - m.forecast)}</td>
                <td className="py-1.5 pr-2 text-right tabular-nums">{m.forecast ? fmt(m.forecast) : "—"}</td>
                <td className="py-1.5 pr-2 text-right font-semibold tabular-nums">{fmt(m.income)}</td>
                <td className="py-1.5 pr-2 text-right tabular-nums">{m.budget > 0 ? fmt(m.budget) : "—"}</td>
                <td className={`py-1.5 text-right font-semibold tabular-nums ${m.net < 0 ? "text-red-500" : "text-emerald-600"}`}>{m.budget > 0 ? fmt(m.net) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className={`text-xs ${c.muted}`}>{tf("crm.note")}</p>
    </section>
  );
}
